import { selectedFields } from '@immosky/shared';
import { FilterState } from '@immosky/types';

export class SimilarObjectSearchQueryBuilder {
  private filterState!: FilterState;
  private sort: any[] = [{ _score: 'desc' }];
  private bool: {
    must: any[];
    should: any[];
    must_not: any[];
    minimum_should_match: number;
  } = {
    must: [],
    should: [],
    must_not: [
      {
        term: { status: 'sold' },
      },
    ],
    minimum_should_match: 0,
  };

  setFilterState(
    filterState: FilterState,
    region_index: string
  ): SimilarObjectSearchQueryBuilder {
    this.filterState = filterState;
    this.appendCountries();
    this.appendIsResidia();
    this.appendExcludedRegions(region_index);
    this.appendContractType();
    this.appendMainCategory();
    this.appendSubCategories();
    this.appendPrice();
    this.appendRoomCount();
    this.appendLivingSpace();
    this.appendUsageSpace();
    this.appendPropertySpace();
    this.appendGeoDistanceSort();


    return this;
  }

  private appendIsResidia() {
    const filter = {
      term: {
        isResidia: this.filterState.isResidia,
      },
    };

    if(this.filterState.isResidia) this.bool.must.push(filter);
  }

  private appendCountries() {
    const filter = {
      terms: {
        countryIsocode: this.filterState.countries,
      },
    };

    this.bool.must.push(filter);
  }
  private appendExcludedRegions(region_index: string) {
    const regions = this.filterState.region.regions;

    if (regions.length > 0) {
      for (const region of regions) {
        this.bool.must_not.push({
          geo_shape: {
            location: {
              indexed_shape: {
                index: region_index,
                id: region.id,
                path: 'geometry',
              },
            },
          },
        });
      }
    }
  }

  private appendContractType() {
    const filter = {
      match: {
        contractType: this.filterState.contractType.filter.value,
      },
    };

    this.bool.must.push(filter);
  }

  private appendMainCategory() {
    const categoryFilter = this.filterState.category.main;

    if (categoryFilter.isActive) {
      const filter = {
        match: {
          category: categoryFilter.filter.value,
        },
      };
      this.bool.should.push(filter);
    }
  }

  private appendSubCategories() {
    const mainCategoryFilter = this.filterState.category.main;
    const subcategoryFilter = this.filterState.category.sub;

    if (subcategoryFilter.isActive) {
      const subcategories =
        subcategoryFilter.filter[mainCategoryFilter.filter.value];
      const activeSubcategories = subcategories.filter((s) => s.isChecked);

      const filter = {
        terms: {
          subCategory: activeSubcategories.map((s) => s.value),
        },
      };

      this.bool.should.push(filter);
    }
  }

  private appendPrice() {
    const priceFilter = this.filterState.price;
    const contractType = this.filterState.contractType.filter.value;

    let priceProperty = 'price.total';
    if (contractType === 'buy') {
      priceProperty = 'price.total';
    } else if (contractType === 'rent') {
      priceProperty = 'price.rentGross';
    }

    if (priceFilter.isActive) {
      const min = priceFilter.filter.min.value;
      const max = priceFilter.filter.max.value;

      const filter: any = {
        range: {
          [priceProperty]: {
            gte: min,
            boost: 1.5,
          },
        },
      };

      if (max !== Infinity) {
        filter.range[priceProperty].lte = max;
      }

      this.bool.should.push(filter);
    }
  }

  private appendRoomCount() {
    const roomCountFilter = this.filterState.roomCount;

    if (roomCountFilter.isActive && roomCountFilter.isDisplayed) {
      const min = roomCountFilter.filter.min.value;
      const max = roomCountFilter.filter.max.value;

      const filter: any = {
        range: {
          rooms: {
            gte: min === Infinity ? 7 : min,
            boost: 1.5,
          },
        },
      };

      if (max !== Infinity) {
        filter.range.rooms.lte = max;
      }

      this.bool.should.push(filter);
    }
  }

  private appendLivingSpace() {
    const livingSpaceFilter = this.filterState.livingSpace;

    if (livingSpaceFilter.isActive && livingSpaceFilter.isDisplayed) {
      const min = livingSpaceFilter.filter.min.value;
      const max = livingSpaceFilter.filter.max.value;

      const filter: any = {
        range: {
          'space.living': {
            gte: min,
            boost: 1.5,
          },
        },
      };

      if (max !== Infinity) {
        filter.range['space.living'].lte = max;
      }

      this.bool.should.push(filter);
    }
  }

  private appendUsageSpace() {
    const usageSpaceFilter = this.filterState.usageSpace;

    if (usageSpaceFilter.isActive && usageSpaceFilter.isDisplayed) {
      const min = usageSpaceFilter.filter.min.value;
      const max = usageSpaceFilter.filter.max.value;

      const filter: any = {
        range: {
          'space.usable': {
            gte: min,
            boost: 1.5,
          },
        },
      };

      if (max !== Infinity) {
        filter.range['space.usable'].lte = max;
      }

      this.bool.should.push(filter);
    }
  }

  private appendPropertySpace() {
    const propertySpaceFilter = this.filterState.propertySpace;

    if (propertySpaceFilter.isActive && propertySpaceFilter.isDisplayed) {
      const min = propertySpaceFilter.filter.min.value;
      const max = propertySpaceFilter.filter.max.value;

      const filter: any = {
        range: {
          'space.property': {
            gte: min,
            boost: 1.5,
          },
        },
      };

      if (max !== Infinity) {
        filter.range['space.property'].lte = max;
      }

      this.bool.should.push(filter);
    }
  }

  private appendGeoDistanceSort() {
    const regions = this.filterState.region.regions;

    if (regions.length > 0) {
      this.sort.push({
        _geo_distance: {
          location: regions.map((r) => {
            return [r.centroid.lng, r.centroid.lat];
          }),
          order: 'asc',
          unit: 'km',
          mode: 'min',
          distance_type: 'plane',
          ignore_unmapped: true,
        },
      });
    }
  }

  build(): any {
    return {
      from: 0,
      size: 20,
      track_scores: true,
      sort: this.sort,
      query: {
        bool: this.bool,
      },
      fields: selectedFields,
      _source: false,
    };
  }
}
